(ns common.schema.entities.counterparty-diligence.templates.capman.natural-person
  (:require
   [common.schema.entities.counterparty-diligence.templates.questions.person :as person-q]
   [common.schema.entities.counterparty-diligence.templates.questions.common :as common-q]
   [common.schema.entities.counterparty-diligence.templates.util :as tu]
   [clojure.string :as string]
   [potpuri.core :as p]))

(def template-id :counterparty-diligence-template/capman-natural-person)

(def yes-no-options
  [{:value :yes
    :label "Yes"}
   {:value :no
    :label "No"}])

(defn find-person
  "Extract person data from natural person template answers for screening.
   NOTE: Capman uses :person/first-names (plural) in their templates,
   but we normalize to :first-name (singular) for screening profile compatibility."
  [{:keys [data]}]
  (let [{:keys [person/first-names person/last-name
                person/date-of-birth person/country]} data
        first-name (-> first-names str string/trim)
        last-name (-> last-name str string/trim)
        countries (tu/nationality-countries data)]
    (when (and (seq first-name) (seq last-name))
      {:first-name    first-name
       :last-name     last-name
       :date-of-birth date-of-birth
       :country       (or (first countries) country)
       :countries     countries})))

(def schema
  {:template/id template-id
   :template/template-name "Natural Person"
   :template/find-person find-person
   :template/form-title    {:en "Transaction Counterparty KYC"}

   :template/notifications
   [{:on-status       :counterparty-diligence.status/review
     :notification-id :notification/counterparty-diligence-review}]
   :template/form-preface
   {:en (str "<p>In accordance with Anti-Money Laundering (AML) and Counter-Terrorist Financing (CTF) regulations, CapMan Group is required to collect specific Know Your Customer (KYC) information on all transaction counterparties.</p>"
             "<br/>"
             "<p>As part of the legal due diligence process, the nominated counsel is requested to gather and review the required documentation of all counterparties of the transaction, and to promptly report any suspicious findings or material observations to CapMan.</p>"
             "<br/>"
             "<p>The Transaction Counterparty KYC check must be carried out on all transaction counterparties:</p>"
             "<ul>"
             "<li>to whom ownership/assets are transferred <strong>directly from CapMan&rsquo;s fund</strong> and if <strong>CapMan&rsquo;s fund is a major counterparty</strong> in the transaction</li>"
             "<li>from whom ownership/assets are transferred <strong>directly to CapMan&rsquo;s fund</strong> and if <strong>CapMan&rsquo;s fund is a major counterparty</strong> in the transaction.</li>"
             "</ul>"
             "<p>Please complete all required information, attach all reviewed materials, and finally confirm the accuracy of the information to the best of your knowledge by signing this form.</p>")}

   :template/schema
   (merge
    (->> [person-q/first-names-singular-label
          person-q/middle-names
          person-q/last-name
          person-q/nationalities
          person-q/date-of-birth
          person-q/proof-of-address-attachment
          person-q/source-of-funds-and-wealth
          person-q/type-of-identification-attachment
          person-q/identification-expiration-date]
         (p/index-by :field))

    {(:field person-q/passport-photo-page-file)
     (merge
      person-q/passport-photo-page-file
      {:visibility-fn (fn [{:keys [data]}]
                        (-> data
                            :person/type-of-identification-attachment
                            (= :identification.type/passport)))})}

    {(:field person-q/id-card-front-file)
     (merge
      person-q/id-card-front-file
      {:visibility-fn (fn [{:keys [data]}]
                        (-> data
                            :person/type-of-identification-attachment
                            (= :identification.type/id-card)))})}

    {(:field person-q/id-card-back-file)
     (merge
      person-q/id-card-back-file
      {:visibility-fn (fn [{:keys [data]}]
                        (-> data
                            :person/type-of-identification-attachment
                            (= :identification.type/id-card)))})}

    {:person-is-politically-exposed
     {:label {:en "Is the person politically exposed?"}
      :data-type :data.type/keyword
      :options yes-no-options}
     (:field common-q/signatories) common-q/signatories})

   :template/sections
   {:basic-information
    {:ord   0
     :title {:en "Basic information"}
     :fields (->> [person-q/first-names-singular-label
                   person-q/middle-names
                   person-q/last-name
                   person-q/nationalities
                   person-q/date-of-birth
                   person-q/proof-of-address-attachment
                   person-q/source-of-funds-and-wealth]
                  (mapv :field))}

    :pep-info
    {:ord 1
     :title {:en "PEP information"}
     :fields [:person-is-politically-exposed]}

    :identification
    {:ord 2
     :title {:en "Identification"}
     :fields (->> [person-q/type-of-identification-attachment
                   person-q/identification-expiration-date
                   person-q/passport-photo-page-file
                   person-q/id-card-front-file
                   person-q/id-card-back-file]
                  (mapv :field))}

    :signatories
    {:ord                3
     :title              {:en "Signatories"}
     :info-text {:en (str "<p>If you have the authority to sign this form, please fill in your name and email below.</p>"
                          "<br/>"
                          "<p>If you do not have the authority to sign this form, please fill in the name and email address of the person from your company who does have the authority to sign.</p>")}
     :info-text-position :top
     :signatory-section? true
     :fields [(:field common-q/signatories)]}}})
